import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Badge, Input } from '../../components/ui';
import { Clock, AlertCircle, CheckCircle } from 'lucide-react';

type TaskType = 'pre-call' | 'post-call' | 'completeness';
type TaskStatus = 'waiting-reviewer' | 'waiting-vendor' | 'processing' | 'past';

interface ReviewTask {
  id: string;
  vendorName: string;
  programName: string;
  programId: string;
  vendorId: string;
  type: TaskType;
  status: TaskStatus;
  daysSinceLastAction: number;
  priority: 'high' | 'medium' | 'low';
  flaggedAsTrash?: boolean;
  missingInfo?: string[];
}

const mockTasks: ReviewTask[] = [
  {
    id: '1',
    vendorName: 'SkyReach Dynamics',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    vendorId: '1',
    type: 'post-call',
    status: 'waiting-reviewer',
    daysSinceLastAction: 2,
    priority: 'high',
  },
  {
    id: '2',
    vendorName: 'Raptor Aerospace',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    vendorId: '2',
    type: 'completeness',
    status: 'waiting-reviewer',
    daysSinceLastAction: 1,
    priority: 'high',
    missingInfo: ['SAM.gov registration not found', 'Missing technical capability details', 'Team information incomplete'],
  },
  {
    id: '3',
    vendorName: 'WingTech Systems',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    vendorId: '5',
    type: 'pre-call',
    status: 'waiting-reviewer',
    daysSinceLastAction: 3,
    priority: 'medium',
    flaggedAsTrash: true,
  },
  {
    id: '4',
    vendorName: 'Horizon Drones',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    vendorId: '8',
    type: 'pre-call',
    status: 'waiting-reviewer',
    daysSinceLastAction: 0,
    priority: 'medium',
  },
  {
    id: '5',
    vendorName: 'AeroSentry',
    programName: 'RFP-2024-001: Aetheris Navigation',
    programId: '1',
    vendorId: '6',
    type: 'post-call',
    status: 'waiting-vendor',
    daysSinceLastAction: 7,
    priority: 'low',
  },
  {
    id: '6',
    vendorName: 'Delta Wing Co',
    programName: 'RFP-2024-003: Hypersonic Defense',
    programId: '4',
    vendorId: '12',
    type: 'pre-call',
    status: 'waiting-reviewer',
    daysSinceLastAction: 1,
    priority: 'medium',
    flaggedAsTrash: true,
  },
];

const ReviewerTasks = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<TaskType | 'all'>('all');

  const filteredTasks = mockTasks.filter((task) => {
    const matchesSearch =
      task.vendorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.programName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesType = typeFilter === 'all' || task.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Sort by priority and days since last action
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    return b.daysSinceLastAction - a.daysSinceLastAction;
  });

  const getTaskTypeLabel = (type: TaskType) => {
    switch (type) {
      case 'pre-call':
        return 'Pre-Call Review';
      case 'post-call':
        return 'Post-Call Review';
      case 'completeness':
        return 'Completeness Check';
    }
  };

  const getTaskTypeColor = (type: TaskType) => {
    switch (type) {
      case 'pre-call':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400';
      case 'post-call':
        return 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400';
      case 'completeness':
        return 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400';
    }
  };

  const getStatusLabel = (status: TaskStatus) => {
    switch (status) {
      case 'waiting-reviewer':
        return 'Waiting on You';
      case 'waiting-vendor':
        return 'Waiting on Vendor';
      case 'processing':
        return 'Processing';
      case 'past':
        return 'Completed';
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'waiting-reviewer':
        return 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400';
      case 'waiting-vendor':
        return 'bg-slate-200 dark:bg-slate-700 text-card-foreground';
      case 'processing':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400';
      case 'past':
        return 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400';
    }
  };

  const handleTaskClick = (task: ReviewTask) => {
    // Navigate to the evaluation page for this task
    navigate(`/review/evaluate/${task.programId}/${task.vendorId}`);
  };

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground">Review Tasks</h1>
          <p className="text-base text-muted-foreground mt-2">
            Your pending reviews sorted by priority and timeliness
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-900 border border-border rounded-lg p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by vendor or program..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as TaskType | 'all')}
                className="px-4 py-2 rounded-md text-sm font-medium border border-input bg-card text-card-foreground"
              >
                <option value="all">All Types</option>
                <option value="post-call">Post-Call</option>
                <option value="pre-call">Pre-Call</option>
                <option value="completeness">Completeness</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as TaskStatus | 'all')}
                className="px-4 py-2 rounded-md text-sm font-medium border border-input bg-card text-card-foreground"
              >
                <option value="all">All Status</option>
                <option value="waiting-reviewer">Waiting on You</option>
                <option value="waiting-vendor">Waiting on Vendor</option>
                <option value="processing">Processing</option>
                <option value="past">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {mockTasks.filter((t) => t.status === 'waiting-reviewer').length}
                </p>
                <p className="text-xs text-muted-foreground">Waiting on You</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {mockTasks.filter((t) => t.type === 'post-call').length}
                </p>
                <p className="text-xs text-muted-foreground">Post-Call Reviews</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/20 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {mockTasks.filter((t) => t.type === 'completeness').length}
                </p>
                <p className="text-xs text-muted-foreground">Completeness Checks</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {mockTasks.filter((t) => t.type === 'pre-call').length}
                </p>
                <p className="text-xs text-muted-foreground">Pre-Call Reviews</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tasks List */}
        <div className="space-y-3">
          {sortedTasks.map((task) => (
            <Card
              key={task.id}
              className={`p-5 hover:shadow-md transition-all cursor-pointer ${
                task.flaggedAsTrash
                  ? 'border-l-4 border-l-red-500 dark:border-l-red-500'
                  : task.priority === 'high'
                  ? 'border-l-4 border-l-blue-600 dark:border-l-blue-600'
                  : ''
              }`}
              onClick={() => handleTaskClick(task)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Flags */}
                  <div className="flex items-center gap-2 mb-2">
                    {task.flaggedAsTrash && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded text-xs font-medium">
                        <AlertCircle className="w-3 h-3" />
                        Flagged as Trash
                      </div>
                    )}
                    {task.missingInfo && task.missingInfo.length > 0 && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded text-xs font-medium">
                        <AlertCircle className="w-3 h-3" />
                        Potentially Incomplete
                      </div>
                    )}
                  </div>

                  {/* Vendor & Program */}
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">
                      {task.vendorName}
                    </h3>
                    <Badge className={getTaskTypeColor(task.type)}>
                      {getTaskTypeLabel(task.type)}
                    </Badge>
                    <Badge className={getStatusColor(task.status)}>
                      {getStatusLabel(task.status)}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {task.programName}
                  </p>

                  {/* Missing Info */}
                  {task.missingInfo && task.missingInfo.length > 0 && (
                    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-3">
                      <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 mb-2">
                        Missing Information:
                      </p>
                      <ul className="space-y-1">
                        {task.missingInfo.map((info, idx) => (
                          <li key={idx} className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                            <span className="text-warning mt-0.5">•</span>
                            <span>{info}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Days Since Last Action */}
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div className="text-right">
                    <p className="font-semibold text-foreground">
                      {task.daysSinceLastAction === 0
                        ? 'Today'
                        : task.daysSinceLastAction === 1
                        ? '1 day'
                        : `${task.daysSinceLastAction} days`}
                    </p>
                    <p className="text-xs text-muted-foreground">since last action</p>
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {sortedTasks.length === 0 && (
            <Card className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-semibold text-foreground mb-2">
                All caught up!
              </p>
              <p className="text-muted-foreground">
                No pending review tasks at the moment.
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewerTasks;

